declare global {
  interface HTMLElementTagNameMap {
    "side-menu-offset": SideMenuOffset;
  }
}

const PANEL_SELECTOR = ".side-drawer__panel.header-new__side-menu-panel";
const SHIPPING_BANNER_SELECTOR =
  ".ge-free-shipping-container.ge-free-shipping-container-desktop";
const SITE_CONTROL_ID = "site-control";
const SEARCH_MODAL_ID = "search-modal-new";
const HEADER_WRAPPER_SELECTOR = ".header-wrapper";

class SideMenuOffset extends HTMLElement {
  private panels: NodeListOf<HTMLElement> | null = null;
  private header: HTMLElement | null = null;
  private siteControl: HTMLElement | null = null;
  private rootDrawer: HTMLElement | null = null;
  private searchModal: HTMLElement | null = null;
  private headerWrapper: HTMLElement | null = null;
  private updateHandler: () => void;
  private searchModalObserver: MutationObserver | null = null;
  private headerObserver: MutationObserver | null = null;
  private shippingBanner: HTMLElement | null = null;
  private shippingBannerObserver: MutationObserver | null = null;
  private shippingBannerResizeObserver: ResizeObserver | null = null;

  constructor() {
    super();
    this.updateHandler = this.update.bind(this);
  }

  connectedCallback(): void {
    this.header = this.closest<HTMLElement>("header-new");
    this.siteControl = document.getElementById(SITE_CONTROL_ID);
    this.observeShippingBanner();
    this.panels = (this.header ?? document).querySelectorAll<HTMLElement>(
      PANEL_SELECTOR,
    );
    this.rootDrawer =
      this.header?.querySelector<HTMLElement>("#side-menu-root") ?? null;
    this.searchModal = document.getElementById(SEARCH_MODAL_ID);
    this.headerWrapper =
      this.header?.closest<HTMLElement>(HEADER_WRAPPER_SELECTOR) ?? null;

    window.addEventListener("resize", this.updateHandler, { passive: true });
    window.addEventListener("scroll", this.updateHandler, { passive: true });
    this.rootDrawer?.addEventListener("side-drawer:open", this.updateHandler);

    if (this.searchModal) {
      this.searchModalObserver = new MutationObserver(this.updateHandler);
      this.searchModalObserver.observe(this.searchModal, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    const headerTarget = this.headerWrapper ?? this.header;
    if (headerTarget) {
      this.headerObserver = new MutationObserver(this.updateHandler);
      this.headerObserver.observe(headerTarget, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    this.update();
  }

  disconnectedCallback(): void {
    window.removeEventListener("resize", this.updateHandler);
    window.removeEventListener("scroll", this.updateHandler);
    this.rootDrawer?.removeEventListener(
      "side-drawer:open",
      this.updateHandler,
    );
    this.searchModalObserver?.disconnect();
    this.headerObserver?.disconnect();
    this.shippingBannerObserver?.disconnect();
    this.shippingBannerResizeObserver?.disconnect();
    this.searchModalObserver = null;
    this.headerObserver = null;
    this.shippingBannerObserver = null;
    this.shippingBannerResizeObserver = null;
  }

  private observeShippingBanner(): void {
    const banner = document.querySelector<HTMLElement>(SHIPPING_BANNER_SELECTOR);
    if (banner) {
      this.attachShippingBanner(banner);
      return;
    }

    this.shippingBannerObserver = new MutationObserver(() => {
      const found = document.querySelector<HTMLElement>(SHIPPING_BANNER_SELECTOR);
      if (found) {
        this.attachShippingBanner(found);
        this.shippingBannerObserver?.disconnect();
        this.shippingBannerObserver = null;
      }
    });
    this.shippingBannerObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private attachShippingBanner(banner: HTMLElement): void {
    this.shippingBanner = banner;
    this.shippingBannerResizeObserver = new ResizeObserver(this.updateHandler);
    this.shippingBannerResizeObserver.observe(banner);
    this.update();
  }

  private update(): void {
    const siteControlHeight = this.siteControl?.offsetHeight ?? 0;
    const shippingBanner = this.shippingBanner?.offsetHeight ?? 0;
    const headerHeight = this.header?.offsetHeight ?? 0;
    const value = `${siteControlHeight + headerHeight + shippingBanner}px`;
    this.panels?.forEach((panel) => {
      panel.style.top = value;
      const overlay = panel
        .closest("side-drawer")
        ?.querySelector<HTMLElement>(".side-drawer__overlay");
      if (overlay) {
        overlay.style.top = value;
      }
    });
    if (this.searchModal) {
      this.searchModal.style.top = value;
    }
  }
}

if (!window.customElements.get("side-menu-offset")) {
  window.customElements.define("side-menu-offset", SideMenuOffset);
}

export {};
